import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Slider,
  TextField,
  Stack,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  Grid,
  ButtonGroup,
  Divider,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LayersIcon from '@mui/icons-material/Layers';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';

import { MapContainer, TileLayer, Marker, Polygon, useMap, useMapEvents } from 'react-leaflet';
import L, { LatLngExpression, LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { AxiosInstance, resolveImageUrl } from '@service/axios-instance';
import { MapOverlayConfig } from '@utils/operation-config-options';
import { RotatedImageOverlay } from '@components/map/leaflet-image-overlay-rotated';
import {
  computeAllPoints,
  panOverlay,
  rotateOverlay,
  scaleOverlay,
  tiltOverlay,
  createDefaultOverlayBounds,
} from '@utils/map-overlay-geometry';

// Fix Leaflet marker icons
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom handle markers
function createHandleIcon(label: string, bg: string, size = 24) {
  return L.divIcon({
    className: 'custom-map-handle',
    html: `<div style="
      background: ${bg};
      color: #fff;
      border-radius: 50%;
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: ${size > 24 ? 12 : 10}px;
      font-weight: 700;
      border: 2px solid #ffffff;
      box-shadow: 0 2px 6px rgba(0,0,0,0.5);
      cursor: grab;
      user-select: none;
    ">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const tlIcon = createHandleIcon('TL', '#1976d2');
const trIcon = createHandleIcon('TR', '#2e7d32');
const blIcon = createHandleIcon('BL', '#ed6c02');
const brIcon = createHandleIcon('BR', '#9c27b0');
const centerIcon = createHandleIcon('✥', '#d32f2f', 28);

interface MapCenterWatcherProps {
  onCenterChange: (center: [number, number]) => void;
}

function MapCenterWatcher({ onCenterChange }: MapCenterWatcherProps) {
  const map = useMapEvents({
    moveend: () => {
      const c = map.getCenter();
      onCenterChange([c.lat, c.lng]);
    },
  });
  return null;
}

function MapFlyTo({ target }: { target: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.setView(target, map.getZoom(), { animate: true });
    }
  }, [target, map]);
  return null;
}

export interface MapOverlayConfigDialogProps {
  open: boolean;
  operationName: string;
  initialConfig?: MapOverlayConfig | null;
  onClose: () => void;
  onSave: (config: MapOverlayConfig) => void;
}

export function MapOverlayConfigDialog({
  open,
  operationName,
  initialConfig,
  onClose,
  onSave,
}: MapOverlayConfigDialogProps) {
  // Config state
  const [enabled, setEnabled] = useState<boolean>(initialConfig?.enabled ?? true);
  const [imageUrl, setImageUrl] = useState<string>(initialConfig?.imageUrl || '');
  const [opacity, setOpacity] = useState<number>(initialConfig?.opacity ?? 0.5); // Default 50% transparency for positioning
  const [locked, setLocked] = useState<boolean>(initialConfig?.locked ?? false);
  const [rotationAngle, setRotationAngle] = useState<number>(initialConfig?.rotation ?? 0);

  // Overlay corner coordinates
  const [topleft, setTopLeft] = useState<[number, number]>(() => {
    if (initialConfig?.topleft && Array.isArray(initialConfig.topleft)) {
      return [initialConfig.topleft[0], initialConfig.topleft[1]];
    }
    return [-21.7644, -48.4042]; // Default EmbraerGP airport coordinates
  });

  const [topright, setTopRight] = useState<[number, number]>(() => {
    if (initialConfig?.topright && Array.isArray(initialConfig.topright)) {
      return [initialConfig.topright[0], initialConfig.topright[1]];
    }
    return [-21.7644, -48.3980];
  });

  const [bottomleft, setBottomLeft] = useState<[number, number]>(() => {
    if (initialConfig?.bottomleft && Array.isArray(initialConfig.bottomleft)) {
      return [initialConfig.bottomleft[0], initialConfig.bottomleft[1]];
    }
    return [-21.7690, -48.4042];
  });

  // UI state
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [tileType, setTileType] = useState<'osm' | 'satellite'>('satellite'); // Satellite is ideal for plant alignment
  const [currentMapCenter, setCurrentMapCenter] = useState<[number, number]>(topleft);
  const [flyToTarget, setFlyToTarget] = useState<[number, number] | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset or initialize when opened
  useEffect(() => {
    if (open) {
      setEnabled(initialConfig?.enabled ?? true);
      setImageUrl(initialConfig?.imageUrl || '');
      setOpacity(initialConfig?.opacity ?? 0.5);
      setLocked(initialConfig?.locked ?? false);
      setRotationAngle(initialConfig?.rotation ?? 0);

      if (initialConfig?.topleft && initialConfig?.topright && initialConfig?.bottomleft) {
        setTopLeft([initialConfig.topleft[0], initialConfig.topleft[1]]);
        setTopRight([initialConfig.topright[0], initialConfig.topright[1]]);
        setBottomLeft([initialConfig.bottomleft[0], initialConfig.bottomleft[1]]);
        const c = computeAllPoints(
          [initialConfig.topleft[0], initialConfig.topleft[1]],
          [initialConfig.topright[0], initialConfig.topright[1]],
          [initialConfig.bottomleft[0], initialConfig.bottomleft[1]]
        ).center;
        setFlyToTarget(c);
      } else {
        // Default around EmbraerGP coordinates
        const def = createDefaultOverlayBounds(-21.765, -48.402, 0.005, 0.008);
        setTopLeft(def.topleft);
        setTopRight(def.topright);
        setBottomLeft(def.bottomleft);
        setFlyToTarget([-21.765, -48.402]);
      }
    }
  }, [open, initialConfig]);

  // Derived points
  const points = useMemo(() => {
    return computeAllPoints(topleft, topright, bottomleft);
  }, [topleft, topright, bottomleft]);

  // Polygon perimeter
  const polygonPositions: LatLngExpression[] = useMemo(() => {
    return [
      [points.topleft[0], points.topleft[1]],
      [points.topright[0], points.topright[1]],
      [points.bottomright[0], points.bottomright[1]],
      [points.bottomleft[0], points.bottomleft[1]],
    ];
  }, [points]);

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 1. Instant local preview so it shows up immediately on map and UI
    const localBlob = URL.createObjectURL(file);
    setImageUrl(localBlob);
    setEnabled(true);

    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('operation', operationName);

      const res = await AxiosInstance.post('/operations/map_overlay/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      if (res.data?.url || res.data?.imageUrl) {
        const uploadedUrl = res.data.url || res.data.imageUrl;
        setImageUrl(uploadedUrl);
      } else {
        throw new Error('Servidor não retornou URL da imagem.');
      }
    } catch (err: any) {
      console.error('Error uploading map overlay:', err);
      // Fallback: Read as base64 data URI if upload endpoint fails or during offline test
      const reader = new FileReader();
      reader.onload = (e) => {
        const b64 = e.target?.result as string;
        if (b64) {
          setImageUrl(b64);
        }
      };
      reader.readAsDataURL(file);
      setUploadError(err?.response?.data?.message || err?.message || 'Erro ao enviar para o servidor, usando pré-visualização local.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Transformation actions
  const handleRotate = (angleDelta: number) => {
    if (locked) return;
    const res = rotateOverlay(topleft, topright, bottomleft, angleDelta);
    setTopLeft(res.topleft);
    setTopRight(res.topright);
    setBottomLeft(res.bottomleft);
    setRotationAngle((prev) => (prev + angleDelta) % 360);
  };

  const handlePan = (dLat: number, dLng: number) => {
    if (locked) return;
    const res = panOverlay(topleft, topright, bottomleft, dLat, dLng);
    setTopLeft(res.topleft);
    setTopRight(res.topright);
    setBottomLeft(res.bottomleft);
  };

  const handleScale = (factor: number) => {
    if (locked) return;
    const res = scaleOverlay(topleft, topright, bottomleft, factor);
    setTopLeft(res.topleft);
    setTopRight(res.topright);
    setBottomLeft(res.bottomleft);
  };

  const handleTilt = (factor: number) => {
    if (locked) return;
    const res = tiltOverlay(topleft, topright, bottomleft, factor);
    setTopLeft(res.topleft);
    setTopRight(res.topright);
    setBottomLeft(res.bottomleft);
  };

  const handleCenterOnMapView = () => {
    if (locked) return;
    const spanLat = Math.abs(topright[0] - bottomleft[0]) || 0.005;
    const spanLng = Math.abs(topright[1] - topleft[1]) || 0.008;
    const bounds = createDefaultOverlayBounds(currentMapCenter[0], currentMapCenter[1], spanLat, spanLng);
    setTopLeft(bounds.topleft);
    setTopRight(bounds.topright);
    setBottomLeft(bounds.bottomleft);
  };

  const handleFlyToOverlay = () => {
    setFlyToTarget([points.center[0], points.center[1]]);
  };

  // Save handler
  const handleSave = () => {
    const config: MapOverlayConfig = {
      enabled,
      imageUrl,
      topleft: [Number(topleft[0].toFixed(7)), Number(topleft[1].toFixed(7))],
      topright: [Number(topright[0].toFixed(7)), Number(topright[1].toFixed(7))],
      bottomleft: [Number(bottomleft[0].toFixed(7)), Number(bottomleft[1].toFixed(7))],
      opacity: Number(opacity.toFixed(2)),
      locked,
      rotation: Math.round(rotationAngle),
    };
    onSave(config);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '92vh', borderRadius: 3 } }}>
      <DialogTitle sx={{ py: 1.5, px: 3, bgcolor: '#111827', color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700} fontFamily="Saira">
              Overlay de Imagem no Mapa — {operationName}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Posicione, rotacione, incline e ajuste a transparência da planta/imagem sobre as coordenadas reais
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
              label={locked ? 'Posição Travada' : 'Edição Desbloqueada'}
              color={locked ? 'success' : 'warning'}
              size="small"
              onClick={() => setLocked(!locked)}
              sx={{ cursor: 'pointer', fontWeight: 600 }}
            />
            <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'hidden' }}>
        {uploadError && (
          <Alert severity="warning" onClose={() => setUploadError(null)} sx={{ py: 0.5 }}>
            {uploadError}
          </Alert>
        )}

        {/* Top Control Bar: Upload & Parameters */}
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Image upload / URL */}
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/png,image/jpeg,image/webp,image/jpg"
                  onChange={handleFileUpload}
                />
                <Button
                  variant="contained"
                  startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  size="small"
                  sx={{ textTransform: 'none', minWidth: 140 }}
                >
                  {uploading ? 'Enviando...' : 'Carregar Imagem'}
                </Button>

                <TextField
                  fullWidth
                  size="small"
                  label="URL da Imagem"
                  placeholder="https://... ou /static/map_overlays/..."
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />

                {imageUrl && (
                  <Tooltip title="Prévia da imagem carregada">
                    <Box
                      component="img"
                      src={resolveImageUrl(imageUrl)}
                      alt="Preview"
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 1,
                        objectFit: 'cover',
                        border: '1px solid #94a3b8',
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Grid>

            {/* Opacity Control (50% default during positioning) */}
            <Grid item xs={12} md={3.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 90 }}>
                  Opacidade: {Math.round(opacity * 100)}%
                </Typography>
                <Slider
                  size="small"
                  value={opacity}
                  min={0.1}
                  max={1.0}
                  step={0.05}
                  onChange={(_, val) => setOpacity(val as number)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(val) => `${Math.round(val * 100)}%`}
                  sx={{ flex: 1 }}
                />
                <ButtonGroup size="small" variant="outlined">
                  <Button
                    onClick={() => setOpacity(0.5)}
                    variant={opacity === 0.5 ? 'contained' : 'outlined'}
                    sx={{ px: 1, fontSize: '0.75rem' }}
                  >
                    50%
                  </Button>
                  <Button
                    onClick={() => setOpacity(0.75)}
                    variant={opacity === 0.75 ? 'contained' : 'outlined'}
                    sx={{ px: 1, fontSize: '0.75rem' }}
                  >
                    75%
                  </Button>
                  <Button
                    onClick={() => setOpacity(1.0)}
                    variant={opacity === 1.0 ? 'contained' : 'outlined'}
                    sx={{ px: 1, fontSize: '0.75rem' }}
                  >
                    100%
                  </Button>
                </ButtonGroup>
              </Stack>
            </Grid>

            {/* Toggles & Tile Selector */}
            <Grid item xs={12} md={3.5}>
              <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
                <ButtonGroup size="small" variant="outlined">
                  <Button
                    onClick={() => setTileType('satellite')}
                    variant={tileType === 'satellite' ? 'contained' : 'outlined'}
                    startIcon={<LayersIcon />}
                  >
                    Satélite
                  </Button>
                  <Button
                    onClick={() => setTileType('osm')}
                    variant={tileType === 'osm' ? 'contained' : 'outlined'}
                  >
                    Ruas
                  </Button>
                </ButtonGroup>

                <FormControlLabel
                  control={
                    <Switch
                      checked={locked}
                      onChange={(e) => setLocked(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {locked ? 'Travado' : 'Destravado'}
                    </Typography>
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Map & Calibration Canvas */}
        <Box sx={{ position: 'relative', flex: 1, minHeight: 400, borderRadius: 2, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
          <MapContainer
            center={points.center}
            zoom={16}
            className={tileType === 'satellite' ? 'no-map-dark-filter' : ''}
            style={{ width: '100%', height: '100%', zIndex: 1 }}
          >
            {/* Tile Layer */}
            {tileType === 'satellite' ? (
              <TileLayer
                attribution="&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            ) : (
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
            )}

            <MapCenterWatcher onCenterChange={setCurrentMapCenter} />
            <MapFlyTo target={flyToTarget} />

            {/* Render Rotated Image Overlay */}
            {imageUrl && enabled && (
              <RotatedImageOverlay
                url={imageUrl}
                topleft={topleft}
                topright={topright}
                bottomleft={bottomleft}
                opacity={opacity}
              />
            )}

            {/* Dashed Polygon Perimeter */}
            <Polygon
              positions={polygonPositions}
              pathOptions={{
                color: locked ? '#10b981' : '#2563eb',
                weight: 2,
                dashArray: locked ? undefined : '6, 6',
                fillOpacity: 0.05,
              }}
            />

            {/* Interactive Draggable Handles (Visible when NOT locked) */}
            {!locked && (
              <>
                {/* Top-Left Handle */}
                <Marker
                  position={topleft}
                  icon={tlIcon}
                  draggable={true}
                  eventHandlers={{
                    drag: (e) => {
                      const pos = e.target.getLatLng();
                      setTopLeft([pos.lat, pos.lng]);
                    },
                  }}
                />

                {/* Top-Right Handle */}
                <Marker
                  position={topright}
                  icon={trIcon}
                  draggable={true}
                  eventHandlers={{
                    drag: (e) => {
                      const pos = e.target.getLatLng();
                      setTopRight([pos.lat, pos.lng]);
                    },
                  }}
                />

                {/* Bottom-Left Handle */}
                <Marker
                  position={bottomleft}
                  icon={blIcon}
                  draggable={true}
                  eventHandlers={{
                    drag: (e) => {
                      const pos = e.target.getLatLng();
                      setBottomLeft([pos.lat, pos.lng]);
                    },
                  }}
                />

                {/* Bottom-Right Handle (controls perspective/tilt/size) */}
                <Marker
                  position={points.bottomright}
                  icon={brIcon}
                  draggable={true}
                  eventHandlers={{
                    drag: (e) => {
                      const pos = e.target.getLatLng();
                      const dLat = pos.lat - points.bottomright[0];
                      const dLng = pos.lng - points.bottomright[1];
                      setTopRight([topright[0] + dLat / 2, topright[1] + dLng / 2]);
                      setBottomLeft([bottomleft[0] + dLat / 2, bottomleft[1] + dLng / 2]);
                    },
                  }}
                />

                {/* Center Handle for Panning */}
                <Marker
                  position={points.center}
                  icon={centerIcon}
                  draggable={true}
                  eventHandlers={{
                    drag: (e) => {
                      const pos = e.target.getLatLng();
                      const dLat = pos.lat - points.center[0];
                      const dLng = pos.lng - points.center[1];
                      handlePan(dLat, dLng);
                    },
                  }}
                />
              </>
            )}
          </MapContainer>

          {/* Floating Calibration Controls Toolbar (Over Map) */}
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              zIndex: 1000,
              p: 1.5,
              borderRadius: 2.5,
              bgcolor: 'rgba(255, 255, 255, 0.94)',
              backdropFilter: 'blur(8px)',
              maxWidth: 420,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
          >
            <Stack spacing={1.5}>
              {/* Header Status */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" fontWeight={700}>
                  Controles de Alinhamento
                </Typography>
                <Chip
                  size="small"
                  label={locked ? 'Controles Bloqueados' : 'Arraste os marcadores ou use os botões'}
                  color={locked ? 'default' : 'primary'}
                  variant="outlined"
                />
              </Stack>

              <Divider />

              {/* Pan (Movement) Controls */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ minWidth: 60, fontWeight: 600 }}>
                  Mover:
                </Typography>
                <IconButton size="small" onClick={() => handlePan(0.0003, 0)} disabled={locked} title="Norte">
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handlePan(-0.0003, 0)} disabled={locked} title="Sul">
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handlePan(0, -0.0003)} disabled={locked} title="Oeste">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handlePan(0, 0.0003)} disabled={locked} title="Leste">
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCenterOnMapView}
                  disabled={locked}
                  startIcon={<CenterFocusStrongIcon />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 'auto' }}
                >
                  Centralizar aqui
                </Button>
              </Stack>

              {/* Rotation Controls */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ minWidth: 60, fontWeight: 600 }}>
                  Rotacionar:
                </Typography>
                <ButtonGroup size="small" variant="outlined" disabled={locked}>
                  <Button onClick={() => handleRotate(-90)}>-90°</Button>
                  <Button onClick={() => handleRotate(-5)}>-5°</Button>
                  <Button onClick={() => handleRotate(-1)}>-1°</Button>
                  <Button onClick={() => handleRotate(1)}>+1°</Button>
                  <Button onClick={() => handleRotate(5)}>+5°</Button>
                  <Button onClick={() => handleRotate(90)}>+90°</Button>
                </ButtonGroup>
              </Stack>

              {/* Scale (Zoom/Size) Controls */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ minWidth: 60, fontWeight: 600 }}>
                  Escala:
                </Typography>
                <ButtonGroup size="small" variant="outlined" disabled={locked}>
                  <Button onClick={() => handleScale(0.95)} startIcon={<ZoomOutIcon />}>
                    -5%
                  </Button>
                  <Button onClick={() => handleScale(0.99)}>-1%</Button>
                  <Button onClick={() => handleScale(1.01)}>+1%</Button>
                  <Button onClick={() => handleScale(1.05)} startIcon={<ZoomInIcon />}>
                    +5%
                  </Button>
                </ButtonGroup>
              </Stack>

              {/* Tilt / Incline Controls */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ minWidth: 60, fontWeight: 600 }}>
                  Inclinar/Tilt:
                </Typography>
                <ButtonGroup size="small" variant="outlined" disabled={locked}>
                  <Button onClick={() => handleTilt(-0.02)}>- Inclinação</Button>
                  <Button onClick={() => handleTilt(0.02)}>+ Inclinação</Button>
                </ButtonGroup>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleFlyToOverlay}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 'auto' }}
                >
                  Ver imagem
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Coordinate status legend */}
          <Paper
            elevation={2}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1000,
              p: 1.2,
              borderRadius: 2,
              bgcolor: 'rgba(17, 24, 39, 0.85)',
              color: 'white',
              fontSize: '0.72rem',
              maxWidth: 280,
            }}
          >
            <Typography variant="caption" fontWeight={700} color="primary.light">
              Marcadores de Controle:
            </Typography>
            <Stack spacing={0.3} mt={0.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1976d2' }} />
                <span>TL: {topleft[0].toFixed(5)}, {topleft[1].toFixed(5)}</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                <span>TR: {topright[0].toFixed(5)}, {topright[1].toFixed(5)}</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ed6c02' }} />
                <span>BL: {bottomleft[0].toFixed(5)}, {bottomleft[1].toFixed(5)}</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#d32f2f' }} />
                <span>Centro / Pan (Mover toda a imagem)</span>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button
          color="error"
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={() => {
            setImageUrl('');
            setEnabled(false);
          }}
          disabled={!imageUrl}
          sx={{ mr: 'auto', textTransform: 'none' }}
        >
          Remover Overlay
        </Button>

        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<CheckIcon />}
          sx={{ textTransform: 'none', px: 3 }}
        >
          Aplicar Configuração
        </Button>
      </DialogActions>
    </Dialog>
  );
}
